/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/templates/useQaTemplatePicker.ts
 * FILE_ID: QA-TEMPLATES-HOOK-001
 * PURPOSE: One picker state machine for desktop dialog and phone sheet.
 * LANDMINE: No hold state. Area rename/add write through existing API only.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { QaApi } from '../../../qa/qaApi'
import { nextDefaultQaNames, normalizeList } from '../../../qa/qaApiHelpers'
import type {
  QaTemplateDetail,
  QaTemplateInsertResult,
  QaTemplateSummary,
} from '../../../qa/qaTemplateTypes'
import {
  BLANK_TEMPLATE,
  BLANK_TEMPLATE_DETAIL,
  BLANK_TEMPLATE_ID,
  countSelection,
  filterCatalog,
  groupByLocationType,
  QA_TEMPLATE_LOCATION_ORDER,
} from './qaTemplateCatalog'
import { toggleItemPick, toggleSectionPick } from './qaTemplateSelection'
import { locationCheckState, openTemplateSeed } from './qaTemplateLocationBasket'
import { loadQaTemplateLibrary } from './qaTemplateLibrary'
import { insertQaTemplateSelection } from './insertQaTemplateSelection'
import { checkTemplateLocation, useMissingTemplateDetails } from './useMissingTemplateDetails'
import { type QaTemplateWriteChoice } from './qaTemplateWriteTarget'
import {
  EMPTY_PRODUCT_DRAFT,
  type QaTemplateProductDraft,
  type QaTemplateProductFocus,
} from './qaTemplateProductDraft'

export type QaTemplatePickerStep = 'types' | 'systems' | 'detail' | 'areas' | 'inserted'

export interface QaTemplateSelection {
  sectionIds: Set<string>
  itemIds: Record<string, Set<string>>
  sectionTitles: Record<string, string>
  itemTitles: Record<string, string>
}

export interface QaTemplatePickerWorkspace {
  activeAreaId: string | null
  activeInspectionId: string | null
  areasQuery: { data?: unknown }
  invalidateExecution: () => void
  invalidateAreas?: () => void
  setAreaMode: (mode: 'build' | 'run' | 'itp') => void
  selectArea: (areaId: string | null) => void
  setActiveChecklistId: (id: string | null) => void
  jobId?: number
  inspections?: { id: string; inspection_name: string }[]
  selectInspection?: (inspectionId: string | null) => void
  openCreatedInspection?: (inspectionId: string) => void
  pendingWriteChoice?: QaTemplateWriteChoice | null
  consumePendingWriteChoice?: () => void
  intoChecklist?: { checklistId: string; areaId: string; name: string } | null
}

function cloneSelection(
  sectionIds: Set<string>,
  itemIds: Record<string, Set<string>>,
  sectionTitles: Record<string, string>,
  itemTitles: Record<string, string>,
): QaTemplateSelection {
  return {
    sectionIds: new Set(sectionIds),
    itemIds: Object.fromEntries(
      Object.entries(itemIds).map(([key, value]) => [key, new Set(value)]),
    ),
    sectionTitles: { ...sectionTitles },
    itemTitles: { ...itemTitles },
  }
}

export function useQaTemplatePicker(
  api: QaApi,
  workspace: QaTemplatePickerWorkspace,
) {
  const [step, setStep] = useState<QaTemplatePickerStep>('types')
  const [query, setQuery] = useState('')
  const [locationType, setLocationType] = useState<string | null>(null)
  const [templateId, setTemplateId] = useState<string | null>(null)
  const [expandedSectionIds, setExpandedSectionIds] = useState<Set<string>>(new Set())
  const [selectedSectionIds, setSelectedSectionIds] = useState<Set<string>>(new Set())
  const [selectedItemIds, setSelectedItemIds] = useState<Record<string, Set<string>>>({})
  const [sectionTitleOverrides, setSectionTitleOverrides] = useState<Record<string, string>>({})
  const [itemTitleOverrides, setItemTitleOverrides] = useState<Record<string, string>>({})
  const [productFocus, setProductFocus] = useState<QaTemplateProductFocus | null>(null)
  const [productDraft, setProductDraft] = useState<QaTemplateProductDraft>(EMPTY_PRODUCT_DRAFT)
  const [targetAreaIds, setTargetAreaIds] = useState<Set<string>>(() => new Set())
  const [pendingAreaNames, setPendingAreaNames] = useState<string[]>([])
  const [templates, setTemplates] = useState<QaTemplateSummary[]>([])
  const [catalogLoaded, setCatalogLoaded] = useState(false)
  const [detailsById, setDetailsById] = useState<Record<string, QaTemplateDetail>>({
    [BLANK_TEMPLATE_ID]: BLANK_TEMPLATE_DETAIL,
  })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<QaTemplateInsertResult | null>(null)
  const [basket, setBasket] = useState<Record<string, QaTemplateSelection>>({})
  const [askWhere, setAskWhere] = useState(false)
  const writeChoice = useRef<QaTemplateWriteChoice | null>(null)
  const newQaName = useRef('')

  const areas = normalizeList<{ id: string; area_name: string }>(workspace.areasQuery.data)
  const detail = templateId ? detailsById[templateId] ?? null : null
  const seededTemplateId = useRef<string | null>(null)
  const basketRef = useRef(basket)
  basketRef.current = basket

  useEffect(() => {
    let cancelled = false
    setCatalogLoaded(false)
    void loadQaTemplateLibrary(api, (rows) => {
      if (!cancelled) setTemplates(rows)
    }, workspace.jobId).then(({ detailsById: next }) => {
      if (!cancelled) {
        setDetailsById((current) => ({ ...current, [BLANK_TEMPLATE_ID]: BLANK_TEMPLATE_DETAIL, ...next }))
      }
    }).catch((err: unknown) => {
      if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load templates.')
    }).finally(() => {
      if (!cancelled) setCatalogLoaded(true)
    })
    return () => { cancelled = true }
  }, [api, workspace.jobId])

  useEffect(() => {
    if (!templateId) return
    const hasItems = Object.values(selectedItemIds).some((set) => set.size > 0)
    if (hasItems) {
      setBasket((current) => ({
        ...current,
        [templateId]: cloneSelection(
          selectedSectionIds,
          selectedItemIds,
          sectionTitleOverrides,
          itemTitleOverrides,
        ),
      }))
      return
    }
    setBasket((current) => {
      if (!(templateId in current)) return current
      const next = { ...current }
      delete next[templateId]
      return next
    })
  }, [templateId, selectedSectionIds, selectedItemIds, sectionTitleOverrides, itemTitleOverrides])

  useEffect(() => {
    if (!detail || seededTemplateId.current === detail.id) return
    seededTemplateId.current = detail.id
    const saved = basketRef.current[detail.id]
    if (saved) {
      setSelectedSectionIds(new Set(saved.sectionIds))
      setSelectedItemIds(
        Object.fromEntries(
          Object.entries(saved.itemIds).map(([key, value]) => [key, new Set(value)]),
        ),
      )
      setSectionTitleOverrides({ ...saved.sectionTitles })
      setItemTitleOverrides({ ...saved.itemTitles })
      setExpandedSectionIds(new Set(detail.sections[0] ? [detail.sections[0].id] : []))
      return
    }
    const seeded = openTemplateSeed(detail)
    setSelectedSectionIds(seeded.selectedSectionIds)
    setSelectedItemIds(seeded.selectedItemIds)
    setExpandedSectionIds(new Set(detail.sections[0] ? [detail.sections[0].id] : []))
    setSectionTitleOverrides({})
    setItemTitleOverrides({})
  }, [detail])

  useMissingTemplateDetails(api, query, locationType, templates, detailsById, setDetailsById)

  const library = useMemo(() => {
    if (!catalogLoaded) return []
    return templates.some((row) => row.id === BLANK_TEMPLATE_ID) ? templates : [BLANK_TEMPLATE, ...templates]
  }, [catalogLoaded, templates])
  const filtered = useMemo(
    () => filterCatalog(library, { ...detailsById, [BLANK_TEMPLATE_ID]: detailsById[BLANK_TEMPLATE_ID] ?? BLANK_TEMPLATE_DETAIL }, query),
    [library, detailsById, query],
  )
  const groups = useMemo(
    () => (catalogLoaded ? groupByLocationType(filtered, { includeBlank: false }) : []),
    [catalogLoaded, filtered],
  )
  const locationTemplates = useMemo(
    () => filtered.filter((row) => (row.location_type || '') === (locationType || '')),
    [filtered, locationType],
  )
  const counts = useMemo(
    () => countSelection(
      detail,
      { sectionIds: selectedSectionIds, itemIds: selectedItemIds },
      Math.max(targetAreaIds.size, 1),
    ),
    [detail, selectedSectionIds, selectedItemIds, targetAreaIds],
  )

  const basketEntries = useMemo(
    () => Object.keys(basket)
      .map((id) => ({ id, detail: detailsById[id], selection: basket[id] }))
      .filter((row): row is { id: string; detail: QaTemplateDetail; selection: QaTemplateSelection } =>
        Boolean(row.detail),
      ),
    [basket, detailsById],
  )
  const basketCounts = useMemo(() => {
    const areaFactor = Math.max(targetAreaIds.size, 1)
    return basketEntries.reduce(
      (sum, row) => {
        const next = countSelection(
          row.detail,
          { sectionIds: row.selection.sectionIds, itemIds: row.selection.itemIds },
          areaFactor,
        )
        return {
          checklists: sum.checklists + next.checklists,
          steps: sum.steps + next.steps,
          stepsTimesAreas: sum.stepsTimesAreas + next.stepsTimesAreas,
        }
      },
      { checklists: 0, steps: 0, stepsTimesAreas: 0 },
    )
  }, [basketEntries, targetAreaIds])
  const basketLocationLabels = useMemo(() => {
    const seen = new Set<string>()
    const ordered = [
      ...QA_TEMPLATE_LOCATION_ORDER,
      ...basketEntries
        .map((row) => row.detail.location_type || '')
        .filter((key) => !QA_TEMPLATE_LOCATION_ORDER.includes(key as typeof QA_TEMPLATE_LOCATION_ORDER[number])),
    ]
    const labels: string[] = []
    for (const key of ordered) {
      for (const row of basketEntries) {
        if ((row.detail.location_type || '') !== key) continue
        const label = row.detail.location_type_label || 'Other'
        if (seen.has(label)) continue
        seen.add(label)
        labels.push(label)
      }
    }
    return labels
  }, [basketEntries])

  const pendingNames = pendingAreaNames.map((name) => name.trim()).filter(Boolean)
  const blankActive = templateId === BLANK_TEMPLATE_ID
  const canInsert = blankActive
    ? Boolean(!busy && (workspace.activeInspectionId || workspace.jobId) && (pendingNames.length > 0 || targetAreaIds.size > 0))
    : Boolean(basketCounts.checklists > 0 && !busy && workspace.activeInspectionId)

  const pickLocation = (value: string) => {
    // The open template belongs to the previous location. Keep its basket entry, drop it from view (B3).
    if (value !== locationType) setTemplateId(null)
    setLocationType(value)
    setProductFocus({ kind: 'location', locationType: value })
    setStep('systems')
  }
  const reload = async () => {
    try {
      const { templates: rows, detailsById: next } = await loadQaTemplateLibrary(api, undefined, workspace.jobId)
      setTemplates(rows)
      setDetailsById((current) => ({ ...current, [BLANK_TEMPLATE_ID]: BLANK_TEMPLATE_DETAIL, ...next }))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load templates.')
    }
  }
  const pickTemplate = (id: string) => {
    setTemplateId(id)
    setStep('detail')
    if (id === BLANK_TEMPLATE_ID) {
      setPendingAreaNames(['Area'])
      setDetailsById((current) => (
        current[BLANK_TEMPLATE_ID] ? current : { ...current, [BLANK_TEMPLATE_ID]: BLANK_TEMPLATE_DETAIL }
      ))
      return
    }
    setPendingAreaNames([])
    if (detailsById[id]) return
    void api.getTemplate(id).then((row) => {
      setDetailsById((current) => ({ ...current, [row.id]: row }))
    }).catch((err: unknown) => {
      setError(err instanceof Error ? err.message : 'Could not load the template.')
    })
  }
  const clearAll = () => {
    setSelectedSectionIds(new Set())
    setSelectedItemIds({})
  }
  const setLocationChecked = (nextLocation: string, on: boolean) => {
    checkTemplateLocation({
      locationType: nextLocation,
      on,
      groups,
      detailsById,
      templateId,
      api,
      setBasket,
      setDetailsById,
      clearAll,
      setSelectedSectionIds,
      setSelectedItemIds,
    })
  }
  const locationChecked = (locationType: string) => locationCheckState(
    groups.find((row) => row.location_type === locationType),
    basket,
  )
  const removeFromBasket = (id: string) => {
    setBasket((current) => {
      if (!(id in current)) return current
      const next = { ...current }
      delete next[id]
      return next
    })
    if (id === templateId) clearAll()
  }
  const toggleSection = (sectionId: string) => {
    const section = detail?.sections.find((row) => row.id === sectionId)
    if (!section) return
    const next = toggleSectionPick({ selectedSectionIds, selectedItemIds }, section)
    setSelectedSectionIds(next.selectedSectionIds)
    setSelectedItemIds(next.selectedItemIds)
  }
  const toggleExpandedSection = (sectionId: string) => {
    setExpandedSectionIds((current) => {
      const next = new Set(current)
      return next.delete(sectionId) ? next : next.add(sectionId)
    })
  }
  const toggleItem = (sectionId: string, itemId: string) => {
    const next = toggleItemPick({ selectedSectionIds, selectedItemIds }, sectionId, itemId)
    setSelectedSectionIds(next.selectedSectionIds)
    setSelectedItemIds(next.selectedItemIds)
  }
  const selectAll = () => {
    if (!detail) return
    const nextSections = new Set<string>()
    const nextItems: Record<string, Set<string>> = {}
    for (const section of detail.sections) {
      nextItems[section.id] = new Set(section.items.map((item) => item.id))
      if (section.items.length > 0) nextSections.add(section.id)
    }
    setSelectedSectionIds(nextSections)
    setSelectedItemIds(nextItems)
  }
  const toggleTarget = (areaId: string) => {
    setTargetAreaIds((current) => {
      const next = new Set(current)
      if (next.has(areaId)) next.delete(areaId)
      else next.add(areaId)
      return next
    })
  }
  const renameTargetArea = async (areaId: string, name: string) => {
    setBusy(true)
    setError(null)
    try {
      await api.renameArea(areaId, name)
      workspace.invalidateExecution()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not rename the area.')
    } finally {
      setBusy(false)
    }
  }
  const addTargetArea = async (name: string) => {
    const trimmed = name.trim()
    if (!trimmed || !workspace.activeInspectionId) return
    setBusy(true)
    setError(null)
    try {
      const area = await api.addTable(workspace.activeInspectionId, trimmed, false)
      workspace.invalidateExecution()
      setTargetAreaIds((current) => new Set(current).add(area.id))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add the area.')
    } finally {
      setBusy(false)
    }
  }
  const renamePendingArea = (index: number, name: string) => {
    setPendingAreaNames((current) => current.map((row, rowIndex) => (rowIndex === index ? name : row)))
  }
  const insertRef = useRef<() => Promise<void> | void>(() => {})
  const answerWriteTarget = (choice: QaTemplateWriteChoice, name?: string) => {
    writeChoice.current = choice
    newQaName.current = name?.trim() || ''
    void insertRef.current()
  }
  const insert = () => insertQaTemplateSelection({
    api,
    workspace,
    blankActive,
    busy,
    pendingNames,
    targetAreaIds,
    basketCounts,
    basketEntries,
    productDraft,
    intoChecklist: workspace.intoChecklist,
    writeChoice,
    newQaName,
    setBusy,
    setError,
    setResult,
    setStep,
    setAskWhere,
    answerWriteTarget,
  })
  insertRef.current = insert

  const reset = useCallback(() => {
    seededTemplateId.current = null
    setStep('types')
    setQuery('')
    setError(null)
    setResult(null)
    setTargetAreaIds(new Set())
    setPendingAreaNames([])
    setBasket({})
    setTemplateId(null)
    setLocationType(null)
  }, [])

  return {
    step,
    setStep,
    query,
    setQuery,
    locationType,
    pickLocation,
    templateId,
    pickTemplate,
    expandedSectionIds,
    toggleExpandedSection,
    expandedSectionId: [...expandedSectionIds][0] ?? null,
    setExpandedSectionId: (id: string | null) => setExpandedSectionIds(new Set(id ? [id] : [])),
    selectedSectionIds,
    selectedItemIds,
    toggleSection,
    toggleItem,
    sectionTitleOverrides,
    itemTitleOverrides,
    setSectionTitle: (id: string, title: string) => setSectionTitleOverrides((current) => ({ ...current, [id]: title })),
    setItemTitle: (id: string, title: string) => setItemTitleOverrides((current) => ({ ...current, [id]: title })),
    targetAreaIds,
    pendingAreaNames,
    renamePendingArea,
    insertNames: [
      ...pendingNames,
      ...areas.filter((area) => targetAreaIds.has(area.id)).map((area) => area.area_name),
    ],
    toggleTarget,
    areas,
    templates: locationTemplates,
    allTemplates: filtered,
    groups,
    detail,
    counts,
    busy,
    error,
    result,
    canInsert,
    selectAll,
    clearAll,
    renameTargetArea,
    addTargetArea,
    insert,
    askWhere,
    answerWriteTarget,
    suggestedQaName: nextDefaultQaNames((workspace.inspections ?? []).map((row) => row.inspection_name), 1, workspace.jobId)[0],
    activeInspectionName: (workspace.inspections ?? []).find((row) => row.id === workspace.activeInspectionId)?.inspection_name || '',
    cancelWriteTarget: () => setAskWhere(false),
    reset,
    reload,
    productFocus,
    setProductFocus,
    productDraft,
    setProductDraft,
    basket,
    basketEntries,
    basketCounts,
    basketLocationLabels,
    removeFromBasket,
    setLocationChecked,
    locationChecked,
    isInBasket: (id: string) => id in basket,
  }
}

export type QaTemplatePicker = ReturnType<typeof useQaTemplatePicker>
